import { DocumentLanguages } from "../../documentTypes"

export type FormFieldType = 'text'
    | 'textarea'
    | 'number'
    | 'date'
    | 'select'
    | 'multiselect'
    | 'checkbox'
    | 'user'
    | 'heading'

export type FormLanguage = 'PL' | 'EN'

export type L10nText = Record<FormLanguage, string>

export interface FormFieldOption {
    /** Immutable once the definition is published: it is what the section stores. */
    value: string
    label: L10nText
}

export interface FormFieldDef {
    /**
     * A guid, because the comment API anchors on `contextGuid` (a `uniqueidentifier` column), so the
     * human-readable `key` cannot serve as the anchor.
     */
    id: string
    /** Immutable once published: the value's key in the section and the docx placeholder suffix. */
    key: string
    type: FormFieldType
    label: L10nText
    /** A visual "missing value" marker only; nothing is ever blocked on it. */
    required?: boolean
    width?: 12 | 6 | 4
    options?: FormFieldOption[]
    help?: L10nText
}

export type FormDefinitionStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'

export interface FormDefinition {
    id: string
    companyId: number
    /** Unique per company and immutable once published: `{{form.<code>.<key>}}`. */
    code: string
    name: string
    status: FormDefinitionStatus
    /** 0 while a draft; 1 on first publish, then +1 per save. Provenance only, there is no history. */
    version: number
    fields: FormFieldDef[]
    /** Absent until the first publish. */
    published?: FormDefinitionPublished
}

/**
 * Every field key and option value that has ever been published. They are locked on the fields that
 * still have them, and reserved for good once removed, so a docx template can never pick up a
 * different field under an old name.
 */
export interface FormDefinitionPublished {
    keys: string[]
    /** Field key → option values. */
    options: Record<string, string[]>
}

const NOTHING_PUBLISHED: FormDefinitionPublished = { keys: [], options: {} }

/** A non-draft without bookkeeping (the mocks) is treated as having published what it has now. */
export const publishedOf = (definition: FormDefinition | undefined): FormDefinitionPublished => {
    if (!definition || definition.status === 'DRAFT') return definition?.published ?? NOTHING_PUBLISHED
    return definition.published ?? mergePublished(NOTHING_PUBLISHED, definition.fields)
}

export const mergePublished = (previous: FormDefinitionPublished, fields: FormFieldDef[]): FormDefinitionPublished => {
    const options: Record<string, string[]> = { ...previous.options }
    for (const field of fields) {
        if (field.options) {
            options[field.key] = Array.from(new Set([...(options[field.key] ?? []), ...field.options.map(o => o.value)]))
        }
    }
    return { keys: Array.from(new Set([...previous.keys, ...fields.map(f => f.key)])), options }
}

/** A user field stores the id and the name at the time of picking, so history needs no lookup. */
export interface FormUserValue {
    userId: string
    fullName: string
}

export type FormFieldValue = string | number | boolean | string[] | FormUserValue | null

/**
 * What a FORM section owns: a copy of the definition's fields taken when the section was created.
 * It is never upgraded to a newer definition version.
 */
export interface FormSectionData {
    sectionId: string
    definitionId: string
    definitionCode: string
    definitionVersion: number
    fields: FormFieldDef[]
    values: Record<string, FormFieldValue>
}

export const isEmptyValue = (value: FormFieldValue | undefined): boolean => {
    if (value === null || value === undefined) return true
    if (typeof value === 'string') return value.trim() === ''
    if (typeof value === 'boolean') return value === false
    if (Array.isArray(value)) return value.length === 0
    if (typeof value === 'object') return value.userId === ''
    return false
}

/**
 * The label in the document's language. A MULTI document shows both, Polish first, and a side that
 * was left untranslated falls back to the other one rather than rendering blank.
 */
export const localizedText = (text: L10nText | undefined, language: DocumentLanguages): string => {
    if (!text) return ''
    const pl = text.PL.trim()
    const en = text.EN.trim()
    switch (language) {
        case 'PL':
            return pl || en
        case 'EN':
            return en || pl
        case 'MULTI':
            return pl && en && pl !== en ? `${pl} / ${en}` : (pl || en)
    }
}
